'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchTravelAgencyTours } from '@eliadmin/core/src/api/travel-agency/travel-agency-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { env } from '@eliadmin/core/src/env';
import { createDebouncedOptionsLoader } from '../utils/debounced-options-loader';

export function useTourOptions() {
  const locale = useLocaleStore(state => state.currentLocale);
  const customerId = useCustomerStore(state => state.me?.customer?.id);
  const client = useQueryClient();
  const [opened, setOpened] = useState(false);
  const scope = JSON.stringify([env.NEXT_PUBLIC_CURRENT_STORE, locale, customerId ?? null]);
  const queryOptions = useMemo(
    () => (search: string) => ({
      queryKey: ['objavuj-tour-options', scope, search],
      queryFn: async () => {
        const response = await fetchTravelAgencyTours({ locale, query: { search } });
        return response.data.map(tour => ({ label: tour.name, value: tour.id.toString() }));
      },
      staleTime: 60_000,
      retry: false as const,
    }),
    [scope, locale],
  );
  const defaults = useQuery({ ...queryOptions(''), enabled: opened, refetchOnWindowFocus: false });
  const loader = useMemo(
    () =>
      createDebouncedOptionsLoader(
        (search: string) => client.fetchQuery(queryOptions(search)),
        400,
      ),
    [client, queryOptions],
  );
  useEffect(() => () => loader.cancel(), [loader]);

  return {
    loadOptions: loader.load,
    defaultOptions: defaults.data ?? [],
    onMenuOpen: () => setOpened(true),
    isLoading: opened && defaults.isFetching,
    cacheOptions: scope,
  };
}
